import LegalPage from "@/pages/legal/LegalPage";

export default function Terms() {
  return (
    <LegalPage title="Terms of Service" updated="February 5, 2026">
      <p>
        Welcome to Immersive Character AI ("<strong>Immersive</strong>", "<strong>we</strong>",
        "<strong>us</strong>", or "<strong>our</strong>"). These Terms of Service ("<strong>Terms</strong>")
        govern your use of our website at <a href="https://imrsvprtl.com">imrsvprtl.com</a> and any
        related services (collectively, the "<strong>Service</strong>"). By accessing or using the
        Service you agree to be bound by these Terms.
      </p>

      <h2>1. Eligibility &amp; Adult Content Disclosure</h2>
      <p>
        <strong>You must be at least 18 years old to use the Service.</strong> The Service is intended
        exclusively for adults and contains fictional roleplay content that may include mature,
        adult, or sexually explicit material when enabled. By using the Service you represent and
        warrant that (a) you are 18 years of age or older, (b) you have the legal capacity to
        enter into these Terms, and (c) accessing adult content is lawful in your jurisdiction.
      </p>
      <p>
        We verify age at signup through a self-attested date of birth and an AI-assisted photo
        verification for accounts that opt in to explicit content. Providing false age
        information is a material breach of these Terms and grounds for immediate termination.
      </p>

      <h2>2. The Service</h2>
      <p>
        Immersive Character AI is an AI-powered fictional-character roleplay platform. Users
        create text-based characters and personas and chat with them. All character responses
        are generated by large language models (currently Anthropic Claude) and are entirely
        fictional. No character on the Service represents a real person unless explicitly
        labeled as public-figure parody.
      </p>

      <h2>3. Accounts</h2>
      <p>
        You must create an account to use most features. You are responsible for keeping your
        credentials secure and for all activity that occurs under your account. We reserve
        the right to suspend or terminate accounts that violate these Terms or applicable law.
      </p>

      <h2>4. Prohibited Content &amp; Conduct</h2>
      <p>You may not create characters, personas, or messages that:</p>
      <ul>
        <li>Depict or describe minors in any sexual context (this includes fictional characters portrayed as under 18);</li>
        <li>Depict non-consensual sexual acts as desirable or unpunished;</li>
        <li>Depict bestiality, incest between close relatives, or other content prohibited by our payment processors;</li>
        <li>Promote real-world violence, self-harm, terrorism, or hate against protected groups;</li>
        <li>Impersonate real people without their consent (public-figure parody must be clearly labeled);</li>
        <li>Contain personally identifiable information about real individuals other than yourself;</li>
        <li>Infringe any third-party intellectual property, trademark, or right of publicity;</li>
        <li>Attempt to jailbreak, exploit, reverse engineer, or overload the Service.</li>
      </ul>
      <p>
        We reserve the right to remove any content and terminate any account at our sole
        discretion. Egregious violations (especially anything involving minors) will be reported
        to appropriate authorities.
      </p>

      <h2>5. Your Content</h2>
      <p>
        You retain ownership of the characters, personas, and chat messages you create ("<strong>User Content</strong>").
        By posting User Content, you grant Immersive Character AI a worldwide, non-exclusive,
        royalty-free license to host, store, display, and process it as necessary to operate
        the Service. You represent that you have all rights necessary to grant this license.
      </p>

      <h2>6. Immersive+ Subscription</h2>
      <p>
        Immersive+ is our paid tier priced at <strong>$14.99 USD per month</strong>, billed
        via Stripe. Immersive+ removes free-tier limits on characters, personas, chats, and
        NSFW gating; unlocks Chat Export, Chat Search, and the Founder badge; and includes
        early access to new features.
      </p>
      <p>
        <strong>Billing.</strong> Charges are processed monthly on the anniversary of your
        initial purchase until you cancel. All fees are non-refundable except as stated in
        our <a href="/refunds">Refund Policy</a>.
      </p>
      <p>
        <strong>Cancellation.</strong> You may cancel at any time from Settings → Subscription.
        Cancellation stops the next billing cycle; you retain Immersive+ benefits until the
        end of the current paid period.
      </p>
      <p>
        <strong>Price changes.</strong> We may change subscription pricing with at least 30
        days' notice via email or in-app notification. Continued use of Immersive+ after the
        change constitutes acceptance.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        The Immersive Character AI name, logo, interface, and underlying software are our
        property or licensed to us and are protected by copyright and trademark law. You may
        not copy, modify, distribute, or create derivative works of any part of the Service
        without prior written permission.
      </p>

      <h2>8. Third-Party Services</h2>
      <p>
        We rely on third-party providers including but not limited to:
      </p>
      <ul>
        <li><strong>Anthropic</strong> (Claude AI models — text generation)</li>
        <li><strong>Google / OpenAI</strong> (image and video generation)</li>
        <li><strong>Stripe</strong> (payment processing)</li>
        <li><strong>Resend</strong> (transactional email delivery)</li>
        <li><strong>MongoDB Atlas</strong> (data storage)</li>
      </ul>
      <p>
        Your use of the Service is also subject to the terms of these providers where applicable.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
        EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. We do not guarantee that AI
        responses will be accurate, appropriate, safe, or free from errors.
      </p>
      <p>
        <strong>AI content is fictional.</strong> Character responses may contain inaccuracies,
        offensive content, or hallucinated facts. Do not rely on AI-generated content for
        medical, legal, financial, or safety-related decisions.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Immersive Character AI shall not be liable
        for any indirect, incidental, special, consequential, or punitive damages, or any
        loss of profits or revenues, whether incurred directly or indirectly. Our total
        aggregate liability for any claim arising out of or relating to these Terms shall
        not exceed the greater of (a) the amount you paid us in the 12 months preceding
        the claim, or (b) US $100.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Immersive Character AI and its officers
        from any claim or demand made by any third party due to or arising out of your
        User Content, your use of the Service, or your violation of these Terms.
      </p>

      <h2>12. Governing Law &amp; Disputes</h2>
      <p>
        These Terms are governed by the laws of the <strong>State of Texas, United States of
        America</strong>, without regard to conflict-of-laws principles. Any dispute arising
        out of or relating to these Terms shall be resolved exclusively in the state or
        federal courts located in Texas, and you consent to their personal jurisdiction.
      </p>

      <h2>13. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at any time, with or without
        cause, and with or without notice. You may terminate your account at any time by
        using the "Delete Account" option in Settings.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        We may modify these Terms from time to time. If we make material changes we will
        notify you by email or in-app notice at least 14 days before the changes take effect.
        Continued use of the Service after the effective date constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? Email us at <a href="mailto:support@imrsvprtl.com">support@imrsvprtl.com</a>{" "}
        or visit our <a href="/contact">Contact page</a>.
      </p>
    </LegalPage>
  );
}
